import { MoreHorizontal, X, GripVertical, Maximize2 } from 'lucide-react';
import React, { useState } from 'react';
import { WidgetInstance } from '../../store/slices/widgetsSlice';

interface BaseWidgetProps {
  widget: WidgetInstance;
  children: React.ReactNode;
  onRemove?: (widgetId: string) => void;
  onConfigChange?: (widgetId: string, config: Record<string, any>) => void;
  onResize?: (widgetId: string, newSize: '1x1' | '2x1' | '2x2') => void;
  onDragStart?: (e: React.DragEvent, widgetId: string) => void;
  onDragOver?: (e: React.DragEvent) => void;
  onDrop?: (e: React.DragEvent, targetWidgetId: string) => void;
  isEditing?: boolean;
  isDragging?: boolean;
  isDragOver?: boolean;
}

const BaseWidget: React.FC<BaseWidgetProps> = ({
  widget,
  children,
  onRemove,
  onResize,
  onDragStart,
  onDragOver,
  onDrop,
  isEditing = false,
  isDragging = false,
  isDragOver = false,
}) => {
  const [showMenu, setShowMenu] = useState(false);
  const [showSizeMenu, setShowSizeMenu] = useState(false);

  const sizes: Array<'1x1' | '2x1' | '2x2'> = ['1x1', '2x1', '2x2'];

  const handleDragStart = (e: React.DragEvent) => {
    if (!isEditing) return;
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', widget.id);
    onDragStart?.(e, widget.id);
  };

  const handleDragOver = (e: React.DragEvent) => {
    if (!isEditing) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    onDragOver?.(e);
  };

  const handleDrop = (e: React.DragEvent) => {
    if (!isEditing) return;
    e.preventDefault();
    onDrop?.(e, widget.id);
  };

  const handleResize = (newSize: '1x1' | '2x1' | '2x2') => {
    onResize?.(widget.id, newSize);
    setShowSizeMenu(false);
  };

  const getSizeClasses = (size: string) => {
    switch (size) {
      case '1x1':
        return 'col-span-6 lg:col-span-3'; // 1 column on mobile, 3 on desktop (12-col grid)
      case '2x1':
        return 'col-span-12 lg:col-span-6'; // 2 columns
      case '2x2':
        return 'col-span-12'; // Full width
      default:
        return 'col-span-6 lg:col-span-3';
    }
  };

  const getHeightClasses = (size: string) => {
    switch (size) {
      case '1x1':
        return 'h-64'; // Increased height for better content display
      case '2x1':
        return 'h-64';
      case '2x2':
        return 'h-80'; // Better proportion for 2x2 widgets
      default:
        return 'h-64';
    }
  };

  return (
    <div
      className={`
        bg-white border rounded-xl shadow-sm
        ${getSizeClasses(widget.size)}
        ${getHeightClasses(widget.size)}
        ${isEditing ? 'ring-2 ring-red-500 ring-opacity-50 cursor-move' : ''}
        ${isDragging ? 'opacity-50 scale-95' : ''}
        ${isDragOver ? 'border-red-500 border-2 border-dashed bg-red-50/30' : 'border-gray-200'}
        relative group hover:shadow-md transition-all duration-200 flex flex-col
      `}
      data-widget-id={widget.id}
      draggable={isEditing}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      onDragEnd={() => {}}
    >
      {/* Widget Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50/50 rounded-t-xl">
        <div className="flex items-center space-x-2 min-w-0">
          {isEditing && (
            <GripVertical className="h-4 w-4 text-gray-400 flex-shrink-0 cursor-grab" />
          )}
          <h3 className="text-sm font-semibold text-gray-900 truncate">{widget.title}</h3>
          <span className="text-xs text-gray-400 flex-shrink-0">{widget.size}</span>
        </div>

        {isEditing && (
          <div className="flex items-center space-x-1">
            {/* Resize Menu */}
            <div className="relative">
              <button
                onClick={() => setShowSizeMenu(!showSizeMenu)}
                className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                aria-label="Resize widget"
                title="Resize widget"
              >
                <Maximize2 className="h-4 w-4" />
              </button>

              {showSizeMenu && (
                <div className="absolute right-0 top-full mt-1 w-28 bg-white border border-gray-200 rounded-lg shadow-lg z-20 py-1">
                  <div className="px-3 py-1.5 text-xs font-medium text-gray-500 border-b border-gray-100">
                    Widget Size
                  </div>
                  {sizes.map(size => (
                    <button
                      key={size}
                      onClick={() => handleResize(size)}
                      className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                        widget.size === size
                          ? 'bg-red-50 text-red-700 font-medium'
                          : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      {size}
                      {widget.size === size && ' ✓'}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Options Menu */}
            <div className="relative">
              <button
                onClick={() => setShowMenu(!showMenu)}
                className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                aria-label="Widget options"
              >
                <MoreHorizontal className="h-4 w-4" />
              </button>

              {showMenu && (
                <div className="absolute right-0 top-full mt-1 w-32 bg-white border border-gray-200 rounded-lg shadow-lg z-20 py-1">
                  <button
                    onClick={() => {
                      onRemove?.(widget.id);
                      setShowMenu(false);
                    }}
                    className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50"
                  >
                    Remove
                  </button>
                </div>
              )}
            </div>

            {/* Quick Remove */}
            <button
              onClick={() => onRemove?.(widget.id)}
              className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
              aria-label="Remove widget"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>

      {/* Widget Content */}
      <div className="flex-1 overflow-hidden">
        <div className="h-full overflow-auto p-4">{children}</div>
      </div>

      {/* Drag indicator strip for editing mode */}
      {isEditing && <div className="absolute top-0 left-0 right-0 h-1 bg-red-500 rounded-t-xl" />}
    </div>
  );
};

export default BaseWidget;
